/**
 * Solar system with precise astronomical positions
 * Calculates the positions of the planets with astronomical precision without an external API
 *
 * Version: 1.0.0
 * Wespify Solutions proprietary code
 * Date: 2025-04-03
 * Auteur: Wespify Solutions
 * Licence: MIT
 *
 * Description: This script calculates the positions of the planets in the solar system using J2000.0 orbital elements.
 * It uses astronomical formulas to determine the position of each planet at a given time.
 * The positions are then applied to DOM elements for visualization.
 * This code is designed for use in a modern web development environment.
 * It is optimized to work with modern JavaScript frameworks such as Vue.js or React.
 * It is also compatible with jQuery for DOM manipulation.
 * This code is intended for use in astronomy research and educational projects.
 */

document.addEventListener('DOMContentLoaded', function() {
  // Class for calculating planetary positions with astronomical precision
  class AstronomicalCalculator {
    constructor() {
      // J2000 orbital elements (source: NASA JPL). Rest of comments will be in my mother tongue, French.
      this.orbitalElements = {
        'mercury': {
          a:    0.38709927,   // demi-grand axe (UA)
          e:    0.20563593,   // excentricité
          i:    7.00497902,   // inclinaison (degrés)
          L:    252.25032350, // longitude moyenne (degrés)
          lp:   77.45779628,  // longitude du périhélie (degrés)
          node: 48.33076593,  // longitude du nœud ascendant (degrés)
          // Variations séculaires
          aVar:    0.00000037,      // variation du demi-grand axe (UA/siècle)
          eVar:    0.00001906,      // variation de l'excentricité (/siècle)
          iVar:    -0.00594749,     // variation de l'inclinaison (degrés/siècle)
          LVar:    149472.67411175, // variation de la longitude moyenne (degrés/siècle)
          lpVar:   0.16047689,      // variation de la longitude du périhélie (degrés/siècle)
          nodeVar: -0.12534081      // variation de la longitude du nœud ascendant (degrés/siècle)
        },
        'venus': {
          a: 0.72333566, e: 0.00677672, i: 3.39467605, L: 181.97909950, lp: 131.60246718, node: 76.67984255,
          aVar: 0.00000390, eVar: -0.00004107, iVar: -0.00078890, LVar: 58517.81538729, lpVar: 0.00268329, nodeVar: -0.27769418
        },
        'earth': {
          a: 1.00000261, e: 0.01671123, i: -0.00001531, L: 100.46457166, lp: 102.93768193, node: 0.0,
          aVar: 0.00000562, eVar: -0.00004392, iVar: -0.01294668, LVar: 35999.37244981, lpVar: 0.32327364, nodeVar: 0.0
        },
        'mars': {
          a: 1.52371034, e: 0.09339410, i: 1.84969142, L: 355.45332150, lp: 336.04084100, node: 49.55953891,
          aVar: 0.00001847, eVar: 0.00007882, iVar: -0.00813131, LVar: 19140.30268499, lpVar: 0.44441088, nodeVar: -0.29257343
        },
        'jupiter': {
          a: 5.20288700, e: 0.04838624, i: 1.30439695, L: 34.39644051, lp: 14.72847983, node: 100.47390909,
          aVar: -0.00011607, eVar: -0.00013253, iVar: -0.00183714, LVar: 3034.74612775, lpVar: 0.21252668, nodeVar: 0.20469106
        },
        'saturn': {
          a: 9.53667594, e: 0.05386179, i: 2.48599187, L: 49.95424423, lp: 92.59887831, node: 113.66242448,
          aVar: -0.00125060, eVar: -0.00050991, iVar: 0.00193609, LVar: 1222.49362201, lpVar: -0.41897216, nodeVar: -0.28867794
        },
        'uranus': {
          a: 19.18916464, e: 0.04725744, i: 0.77263783, L: 313.23810451, lp: 170.95427630, node: 74.01692503,
          aVar: -0.00196176, eVar: -0.00004397, iVar: -0.00242939, LVar: 428.48202785, lpVar: 0.40805281, nodeVar: 0.04240589
        },
        'neptune': {
          a: 30.06992276, e: 0.00859048, i: 1.77004347, L: 304.88003558, lp: 44.96476227, node: 131.78422574,
          aVar: 0.00026291, eVar: 0.00005105, iVar: 0.00035372, LVar: 218.45945325, lpVar: -0.32241464, nodeVar: -0.00508664
        },
        'pluto': {
          a: 39.48211675, e: 0.24882730, i: 17.14001206, L: 238.92903833, lp: 224.06891629, node: 110.30393684,
          aVar: -0.00031596, eVar: 0.00005170, iVar: 0.00004818, LVar: 145.20780515, lpVar: -0.04062942, nodeVar: -0.01183482
        },
        // Pour la ceinture d'astéroïdes, nous utilisons une valeur moyenne approximative
        'asteroids-belt': {
          a: 2.8, e: 0.1, i: 0, L: 150, lp: 0, node: 0,
          aVar: 0, eVar: 0, iVar: 0, LVar: 9000, lpVar: 0, nodeVar: 0
        }
      };
    }

    // Convertir une date en siècles juliens depuis J2000.0
    getJulianCenturiesSinceJ2000(date) {
      // Date de référence J2000.0 = 1er janvier 2000 à 12:00 TT
      const j2000 = new Date('2000-01-01T12:00:00Z');

      // Calculer la différence en jours
      const daysSinceJ2000 = (date.getTime() - j2000.getTime()) / (1000 * 60 * 60 * 24);

      // Convertir en siècles juliens
      return daysSinceJ2000 / 36525;
    }

    // Normaliser un angle en degrés entre 0 et 360
    normalizeAngle(angle) {
      return ((angle % 360) + 360) % 360;
    }

    // Convertir des degrés en radians
    toRadians(degrees) {
      return degrees * Math.PI / 180;
    }

    // Convertir des radians en degrés
    toDegrees(radians) {
      return radians * 180 / Math.PI;
    }

    // Calculer la position d'une planète à un moment donné
    calculatePlanetPosition(planet, date) {
      const T = this.getJulianCenturiesSinceJ2000(date);
      const elements = this.orbitalElements[planet];

      if (!elements) {
        console.error(`Éléments orbitaux non définis pour ${planet}`);
        return { longitude: 0 };
      }

      // Calculer les éléments orbitaux à l'époque donnée
      const a = elements.a + elements.aVar * T;
      const e = elements.e + elements.eVar * T;
      const i = elements.i + elements.iVar * T;
      const L = elements.L + elements.LVar * T;
      const lp = elements.lp + elements.lpVar * T;
      const node = elements.node + elements.nodeVar * T;

      // Anomalie moyenne (M) à partir de la longitude moyenne (L) et de la longitude du périhélie (lp)
      const M = this.normalizeAngle(L - lp);

      // Résoudre l'équation de Kepler pour l'anomalie excentrique (E)
      let E = M; // Première approximation
      const maxIterations = 100;
      const precision = 1e-10;

      for (let i = 0; i < maxIterations; i++) {
        const E_next = E - (E - e * Math.sin(this.toRadians(E)) - M) / (1 - e * Math.cos(this.toRadians(E)));

        if (Math.abs(E_next - E) < precision) {
          E = E_next;
          break;
        }

        E = E_next;
      }

      // Calculer l'anomalie vraie (v)
      const xv = Math.cos(this.toRadians(E)) - e;
      const yv = Math.sqrt(1 - e * e) * Math.sin(this.toRadians(E));
      const v = this.normalizeAngle(this.toDegrees(Math.atan2(yv, xv)));

      // Calculer la longitude héliocentrique
      const helioLong = this.normalizeAngle(v + lp);

      // Correction pour la visualisation (rotation de la visualisation pour correspondre au point de vue terrestre)
      // Pour une visualisation plus claire, on peut ajouter un décalage
      const visualAngle = this.normalizeAngle(helioLong);

      return { longitude: visualAngle };
    }

    // Calculer les positions de toutes les planètes à un moment donné
    calculateAllPlanetPositions(date) {
      const positions = {};

      for (const planet in this.orbitalElements) {
        positions[planet] = this.calculatePlanetPosition(planet, date);
      }

      return positions;
    }

    // Calculer la date correspondant à une position donnée de la Terre
    calculateDateFromEarthPosition(longitude) {
      // Date de référence J2000.0
      const j2000 = new Date('2000-01-01T12:00:00Z');

      // La période orbitale de la Terre est d'environ 365.25 jours
      // Calculer combien de temps s'est écoulé depuis J2000 pour atteindre cette longitude
      const earthLongitudeAtJ2000 = this.orbitalElements.earth.L;
      const deltaLongitude = this.normalizeAngle(longitude - earthLongitudeAtJ2000);

      // Convertir la différence de longitude en jours (un tour complet = 365.25 jours)
      const daysElapsed = (deltaLongitude / 360) * 365.25;

      // Créer la nouvelle date
      return new Date(j2000.getTime() + daysElapsed * 24 * 60 * 60 * 1000);
    }

    // Nouvelle méthode pour avancer le temps
    advanceTime(startDate, elapsedSeconds) {
      // Crée une nouvelle date en avançant par secondes
      return new Date(startDate.getTime() + (elapsedSeconds * 1000));
    }

    // Estimer la date astronomique en fonction des angles de rotation des planètes
    calculateAstronomicalDate(earthRotation) {
      // Extraire l'angle de --initial-rotation et soustraire la rotation CSS
      const cssRotation = parseFloat(earthRotation) || 0;

      // Calculer la longitude terrestre à partir de la rotation CSS
      // Note: il faut ajuster cela selon l'implementation de l'animation CSS
      const earthLongitude = this.normalizeAngle(cssRotation);

      // Calculer la date correspondante
      return this.calculateDateFromEarthPosition(earthLongitude);
    }
  }

  // Variables pour le temps
  let simulationStartTime = new Date();
  let realStartTime = Date.now();

  // Ajustement du facteur d'accélération pour correspondre à l'animation CSS réelle
  // Au lieu d'un jour par seconde, nous allons ajuster pour que
  // une année complète corresponde à la durée de l'animation CSS de la Terre
  const timeScale = 365.25 / 30; // Une année terre en 30 secondes (correspondant à revolution() dans le SCSS)

  // Fonction principale pour initialiser les positions planétaires
  function initPreciseAstronomicalPositions() {
    // Créer une instance du calculateur astronomique
    const calculator = new AstronomicalCalculator();

    // Obtenir la date actuelle
    const now = new Date();
    simulationStartTime = new Date(now);
    realStartTime = Date.now();

    // Calculer les positions des planètes
    const planetData = calculator.calculateAllPlanetPositions(now);

    // Appliquer les positions aux éléments DOM
    for (const [planet, position] of Object.entries(planetData)) {
      const element = document.querySelector(`.${planet}`);
      if (element) {
        // Ajuster l'angle pour notre système d'animation CSS
        element.style.setProperty('--initial-rotation', `${position.longitude}deg`);
      }
    }

    // Marquer le système comme initialisé
    document.querySelector('.solar-syst').classList.add('initialized');

    // Démarrer l'animation de temps
    animateTime(calculator);
  }

  // Fonction pour animer le temps et mettre à jour l'affichage
  function animateTime(calculator) {
    const dateDisplay = document.getElementById('astronomical-date');
    if (!dateDisplay) {
      // Si l'élément d'affichage n'existe pas, on le crée juste après .solar-syst
      const solarSystem = document.querySelector('.solar-syst');
      if (solarSystem) {
        const dateDiv = document.createElement('div');
        dateDiv.className = 'astronomical-date';
        dateDiv.innerHTML = 'Date astronomique : <span id="astronomical-date">Synchronisation en cours...</span>';
        solarSystem.insertAdjacentElement('afterend', dateDiv);
      }
    }

    // Fonction de mise à jour du temps (appelée à chaque frame)
    function updateTime() {
      // Récupérer l'élément Terre pour déterminer sa position actuelle
      const earthElement = document.querySelector('.earth');
      let currentPosition = 0;

      if (earthElement) {
        // Obtenir la rotation CSS actuelle de la Terre
        const computedStyle = window.getComputedStyle(earthElement);
        const transform = computedStyle.getPropertyValue('transform');

        if (transform && transform !== 'none') {
          const values = transform.match(/matrix\((.+)\)/);
          if (values && values[1]) {
            const parts = values[1].split(', ');
            if (parts.length >= 4) {
              const a = parseFloat(parts[0]);
              const b = parseFloat(parts[1]);
              currentPosition = Math.atan2(b, a) * (180 / Math.PI);
            }
          }
        }
      }

      // Convertir la position CSS en date
      // Position initiale + rotation = position actuelle
      const earthInitialRotation = parseFloat(earthElement?.style.getPropertyValue('--initial-rotation')) || 0;
      const earthCurrentRotation = currentPosition;

      // Calculer l'angle relatif (combien la Terre a tourné depuis le début)
      // Ajuster pour la direction de rotation (sens horaire en CSS)
      let angle = (earthInitialRotation - earthCurrentRotation) % 360;
      if (angle < 0) angle += 360;

      // Convertir cet angle en fraction d'année
      const yearFraction = angle / 360;

      // Calculer la date correspondante (une rotation complète = 1 année)
      const currentDate = new Date(simulationStartTime);
      currentDate.setDate(currentDate.getDate() + Math.floor(yearFraction * 365.25));

      // Récupérer l'élément d'affichage de la date (peut avoir été créé dynamiquement)
      const dateElement = document.getElementById('astronomical-date');

      if (dateElement) {
        // Format de date personnalisé avec 3 lettres pour le mois et un point en anglais
        // Ajouter un zéro devant le jour s'il est inférieur à 10
        const day = currentDate.getUTCDate();
        const formattedDay = day < 10 ? `0${day}` : day;

        // Tableau des mois en 3 lettres (en anglais)
        const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May.', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
        const month = months[currentDate.getUTCMonth()];

        const year = currentDate.getUTCFullYear();

        // Utiliser une structure HTML avec des éléments à largeur fixe
        dateElement.innerHTML = `
          <span class="date-part day">${formattedDay}</span>
          <span class="date-part month">${month}</span>
          <span class="date-part year">${year}</span>
        `;

        // Ajouter des classes CSS pour l'animation
        if (!dateElement.classList.contains('active')) {
          dateElement.classList.add('active');
        }
      }

      // Utiliser requestAnimationFrame pour une animation fluide
      requestAnimationFrame(updateTime);
    } // Fin de la fonction updateTime

    // Démarrer la boucle d'animation
    updateTime();
  }

  // Récupération des positions orbitales courantes de l'animation CSS
  function getOrbitPositionFromCSS(planetElement) {
    if (!planetElement) return 0;

    // Récupérer le style calculé
    const computedStyle = window.getComputedStyle(planetElement);
    const transform = computedStyle.getPropertyValue('transform');

    // Extraire l'angle de rotation de la matrice de transformation
    // Format: matrix(a, b, c, d, tx, ty) où l'angle est atan2(b, a)
    if (transform && transform !== 'none') {
      const values = transform.match(/matrix\((.+)\)/);
      if (values && values[1]) {
        const parts = values[1].split(', ');
        if (parts.length >= 4) {
          const a = parseFloat(parts[0]);
          const b = parseFloat(parts[1]);
          const angle = Math.atan2(b, a) * (180 / Math.PI);
          return angle;
        }
      }
    }

    // Fallback: récupérer la propriété CSS personnalisée --initial-rotation
    const initialRotation = planetElement.style.getPropertyValue('--initial-rotation');
    return parseFloat(initialRotation) || 0;
  }

  // Lancer les calculs de position astronomiques
  initPreciseAstronomicalPositions();
});
